// Enhanced Expense Export Service with VAT Breakdown
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/formatting';
import { expensesService } from '../accounting/expenses';
import { companySettingsService } from '../system/company-settings';

export const expenseExportService = {
  /**
   * Generate Excel report for expenses with VAT breakdown
   * @param {string} companyId - Company ID
   * @param {Object} options - Export options
   * @param {Array} expenses - Filtered expenses to export
   * @param {string} language - Current language (en/ar)
   * @returns {Blob} Excel file blob
   */
  async generateExpenseReport(companyId, options = {}) {
    try {
      console.log('🔄 Generating expense Excel report with VAT breakdown...');
      
      // Get company information
      const companySettings = await companySettingsService.getCompanySettings(companyId);
      
      // Get expenses data
      const allExpenses = await expensesService.getExpenses(companyId);
      
      // Filter expenses by date range and other criteria if specified
      let filteredExpenses = allExpenses.filter(expense => {
        const expenseDate = this.getExpenseDate(expense);
        const inDateRange = (!options.startDate || expenseDate >= options.startDate) &&
                           (!options.endDate || expenseDate <= options.endDate);
        const matchesCategory = !options.categoryFilter || options.categoryFilter === 'all' || expense.category === options.categoryFilter;
        const matchesStatus = !options.statusFilter || options.statusFilter === 'all' || expense.status === options.statusFilter;
        return inDateRange && matchesCategory && matchesStatus;
      });

      // Sort expenses by date (newest first)
      filteredExpenses = filteredExpenses.sort((a, b) => {
        const dateA = this.getExpenseDate(a);
        const dateB = this.getExpenseDate(b);
        return dateB - dateA;
      });
      
      // Prepare data for Excel with VAT breakdown
      const reportData = this.prepareExpenseData(filteredExpenses, options.language || 'en');
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Create combined sheet with header and data
      const combinedSheet = this.createCombinedSheet(companySettings, options, reportData, options.language || 'en');
      
      XLSX.utils.book_append_sheet(workbook, combinedSheet, 'Expenses Report');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      console.log('✅ Expense Excel report generated successfully');
      return blob;
      
    } catch (error) {
      console.error('❌ Error generating expense Excel report:', error);
      throw error;
    }
  },

  /**
   * Generate CSV report for expenses with VAT breakdown
   * @param {Array} expenses - Filtered expenses to export
   * @param {string} language - Current language (en/ar)
   * @returns {Blob} CSV file blob
   */
  generateExpenseCSV(expenses, language = 'en') {
    try {
      console.log('🔄 Generating expense CSV report with VAT breakdown...');
      
      // Prepare data for CSV
      const reportData = this.prepareExpenseData(expenses, language);
      
      if (reportData.length === 0) {
        throw new Error('No data to export');
      }

      // Convert to CSV
      const headers = Object.keys(reportData[0]);
      const csvContent = [
        headers.join(','),
        ...reportData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape commas and quotes in CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Create blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      console.log('✅ Expense CSV report generated successfully');
      return blob;
      
    } catch (error) {
      console.error('❌ Error generating expense CSV report:', error);
      throw error;
    }
  },

  /**
   * Prepare expense data for export with VAT breakdown
   */
  prepareExpenseData(expenses, language = 'en') {
    const headers = this.getBilingualHeaders(language);
    
    return expenses.map(expense => {
      const expenseDate = this.getExpenseDate(expense);
      const dateFormatted = formatDate(expenseDate);

      // Get category information
      const categoryInfo = this.getCategoryInfo(expense.category, language);
      
      // Calculate VAT breakdown if not already present
      const vatBreakdown = this.calculateVATBreakdown(expense);
      
      return {
        [headers.date]: dateFormatted,
        [headers.category]: categoryInfo.name,
        [headers.vendor]: expense.vendor || 'N/A',
        [headers.description]: expense.description || '',
        [headers.baseAmount]: vatBreakdown.baseAmount,
        [headers.vatAmount]: vatBreakdown.vatAmount,
        [headers.totalAmount]: vatBreakdown.totalAmount,
        [headers.vatIncluded]: expense.vatIncluded !== undefined 
          ? (expense.vatIncluded ? (language === 'ar' ? 'نعم' : 'Yes') : (language === 'ar' ? 'لا' : 'No'))
          : 'N/A',
        [headers.vatCalculationMethod]: this.getVATCalculationMethod(expense, language),
        [headers.status]: this.getStatusText(expense.status, language),
        [headers.paymentMethod]: expense.paymentMethod || '',
        [headers.reference]: expense.reference || '',
        [headers.receipt]: expense.receiptAttached ? (language === 'ar' ? 'نعم' : 'Yes') : (language === 'ar' ? 'لا' : 'No'),
        [headers.expenseId]: this.generateExpenseId(expense),
        [headers.createdBy]: expense.createdBy === 'system' ? (language === 'ar' ? 'تلقائي' : 'Automatic') : (language === 'ar' ? 'يدوي' : 'Manual'),
        [headers.shipmentReference]: this.getShipmentReference(expense)
      };
    });
  },

  /**
   * Create combined sheet with header and data
   */
  createCombinedSheet(companySettings, options, reportData, language = 'en') {
    const ws = XLSX.utils.aoa_to_sheet([]) || {};
    
    // Ensure worksheet has required properties
    if (!ws['!cols']) ws['!cols'] = [];
    if (!ws['!rows']) ws['!rows'] = [];
    if (!ws['!ref']) ws['!ref'] = 'A1';
    
    // Company header (row 1-2)
    const companyName = companySettings?.companyName || (language === 'ar' ? 'اسم الشركة' : 'Company Name');
    XLSX.utils.sheet_add_aoa(ws, [
      [companyName],
      []
    ], { origin: 'A1' });
    
    // Report title and date info (rows 3-5)
    const reportTitle = language === 'ar' ? 'تقرير المصروفات مع تفصيل ضريبة القيمة المضافة' : 'EXPENSES REPORT WITH VAT BREAKDOWN';
    const reportDateLabel = language === 'ar' ? 'تاريخ التقرير:' : 'Report Date:';
    const dataRangeLabel = language === 'ar' ? 'نطاق البيانات:' : 'Data Range:';
    
    const allTimeLabel = language === 'ar' ? 'جميع الأوقات' : 'All Time';
    const startDate = options.startDate ? formatDate(options.startDate) : allTimeLabel;
    const endDate = options.endDate ? formatDate(options.endDate) : allTimeLabel;
    const dateRange = options.startDate || options.endDate ? `${startDate} - ${endDate}` : allTimeLabel;

    XLSX.utils.sheet_add_aoa(ws, [
      [reportTitle],
      [`${reportDateLabel} ${formatDate(new Date())}`],
      [`${dataRangeLabel} ${dateRange}`],
      []
    ], { origin: 'A3' });
    
    // Add data starting from row 7
    if (reportData.length > 0) {
      // Get headers from first data row
      const headers = Object.keys(reportData[0]);
      
      // Add headers
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });
      
      // Add data rows
      const dataRows = reportData.map(row => headers.map(header => row[header]));
      XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

      ws['!ref'] = XLSX.utils.encode_range
        ? XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 7 + dataRows.length, c: headers.length - 1 } })
        : `A1:${XLSX.utils.encode_cell({ r: 7 + dataRows.length, c: headers.length - 1 })}`;
    }
    
    // Apply styling
    this.applySheetStyling(ws, reportData.length, language);
    
    return ws;
  },

  /**
   * Apply styling to the sheet
   */
  applySheetStyling(ws, dataRowCount, language = 'en') {
    // Set column widths
    const columnWidths = this.getColumnWidths();
    ws['!cols'] = columnWidths;
    
    // Set row heights
    ws['!rows'] = [
      { hpt: 30 }, // Company name
      { hpt: 15 }, // Empty row
      { hpt: 25 }, // Report title
      { hpt: 18 }, // Report date
      { hpt: 18 }, // Date range
      { hpt: 15 }, // Empty row
      { hpt: 35 }  // Data headers
    ];
    
    // Style company name (A1)
    if (!ws['A1']) ws['A1'] = { v: '' };
    ws['A1'].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F4E79' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { 
        fgColor: { rgb: 'F8F9FA' },
        patternType: 'solid'
      }
    };
    
    // Style report title (A3)
    if (!ws['A3']) ws['A3'] = { v: '' };
    ws['A3'].s = {
      font: { bold: true, sz: 16, color: { rgb: '2E7D32' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { 
        fgColor: { rgb: 'F1F8E9' },
        patternType: 'solid'
      }
    };
    
    // Style data headers and rows
    if (dataRowCount > 0) {
      const headerRow = 7;
      const fallbackEndCol = Math.max((ws['!cols']?.length || 1) - 1, 0);
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1') || {
        s: { c: 0, r: 0 },
        e: { c: fallbackEndCol, r: headerRow + dataRowCount - 1 }
      };
      
      // Style headers
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: headerRow - 1, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
        ws[cellAddress].s = {
          font: { 
            bold: true, 
            sz: 12,
            color: { rgb: 'FFFFFF' }
          },
          fill: { 
            fgColor: { rgb: '1565C0' },
            patternType: 'solid'
          },
          border: {
            top: { style: 'medium', color: { rgb: '0D47A1' } },
            bottom: { style: 'medium', color: { rgb: '0D47A1' } },
            left: { style: 'thin', color: { rgb: '0D47A1' } },
            right: { style: 'thin', color: { rgb: '0D47A1' } }
          },
          alignment: { 
            horizontal: 'center', 
            vertical: 'center',
            wrapText: true
          }
        };
      }
      
      // Style data rows
      for (let row = headerRow; row < headerRow + dataRowCount; row++) {
        const isEvenRow = (row - headerRow) % 2 === 0;
        const backgroundColor = isEvenRow ? 'FFFFFF' : 'F8F9FA';
        
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          
          const cellValue = ws[cellAddress].v;
          const isNumeric = typeof cellValue === 'number' || (!isNaN(parseFloat(cellValue)) && isFinite(cellValue));
          
          ws[cellAddress].s = {
            font: { sz: 10, color: { rgb: '212121' } },
            fill: { 
              fgColor: { rgb: backgroundColor },
              patternType: 'solid'
            },
            border: {
              top: { style: 'thin', color: { rgb: 'E0E0E0' } },
              bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
              left: { style: 'thin', color: { rgb: 'E0E0E0' } },
              right: { style: 'thin', color: { rgb: 'E0E0E0' } }
            },
            alignment: { 
              horizontal: isNumeric ? 'right' : 'left', 
              vertical: 'center',
              wrapText: true
            }
          };
          
          // Apply number formatting for numeric values
          if (isNumeric && cellValue !== 0) {
            const numValue = parseFloat(cellValue);
            const hasDecimals = numValue % 1 !== 0;
            ws[cellAddress].z = hasDecimals ? '#,##0.00' : '#,##0';
          }
        }
      }
    }
  },

  /**
   * Get bilingual headers for expense export
   */
  getBilingualHeaders(language = 'en') {
    if (language === 'ar') {
      return {
        date: 'التاريخ | Date',
        category: 'الفئة | Category',
        vendor: 'المورد | Vendor',
        description: 'الوصف | Description',
        baseAmount: 'المبلغ الأساسي | Base Amount (SAR)',
        vatAmount: 'ضريبة القيمة المضافة | VAT Amount (SAR)',
        totalAmount: 'المبلغ الإجمالي | Total Amount (SAR)',
        vatIncluded: 'شامل ضريبة القيمة المضافة | VAT Included',
        vatCalculationMethod: 'طريقة حساب الضريبة | VAT Calculation Method',
        status: 'الحالة | Status',
        paymentMethod: 'طريقة الدفع | Payment Method',
        reference: 'المرجع | Reference',
        receipt: 'الإيصال | Receipt',
        expenseId: 'رقم المصروف | Expense ID',
        createdBy: 'أنشئ بواسطة | Created By',
        shipmentReference: 'مرجع الشحنة | Shipment Reference'
      };
    } else {
      return {
        date: 'Date | التاريخ',
        category: 'Category | الفئة',
        vendor: 'Vendor | المورد',
        description: 'Description | الوصف',
        baseAmount: 'Base Amount (SAR) | المبلغ الأساسي',
        vatAmount: 'VAT Amount (SAR) | ضريبة القيمة المضافة',
        totalAmount: 'Total Amount (SAR) | المبلغ الإجمالي',
        vatIncluded: 'VAT Included | شامل ضريبة القيمة المضافة',
        vatCalculationMethod: 'VAT Calculation Method | طريقة حساب الضريبة',
        status: 'Status | الحالة',
        paymentMethod: 'Payment Method | طريقة الدفع',
        reference: 'Reference | المرجع',
        receipt: 'Receipt | الإيصال',
        expenseId: 'Expense ID | رقم المصروف',
        createdBy: 'Created By | أنشئ بواسطة',
        shipmentReference: 'Shipment Reference | مرجع الشحنة'
      };
    }
  },

  /**
   * Get column widths for expense export
   */
  getColumnWidths() {
    return [
      { wch: 15 },  // Date
      { wch: 20 },  // Category
      { wch: 20 },  // Vendor
      { wch: 35 },  // Description
      { wch: 18 },  // Base Amount
      { wch: 18 },  // VAT Amount
      { wch: 18 },  // Total Amount
      { wch: 20 },  // VAT Included
      { wch: 25 },  // VAT Calculation Method
      { wch: 15 },  // Status
      { wch: 18 },  // Payment Method
      { wch: 15 },  // Reference
      { wch: 12 },  // Receipt
      { wch: 15 },  // Expense ID
      { wch: 15 },  // Created By
      { wch: 20 }   // Shipment Reference
    ];
  },

  /**
   * Helper functions
   */
  getExpenseDate(expense) {
    if (expense.date) {
      return expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
    } else if (expense.expenseDate) {
      return expense.expenseDate?.toDate ? expense.expenseDate.toDate() : new Date(expense.expenseDate);
    } else if (expense.createdAt) {
      return expense.createdAt?.toDate ? expense.createdAt.toDate() : new Date(expense.createdAt);
    } else {
      return new Date();
    }
  },

  calculateVATBreakdown(expense) {
    // If VAT breakdown already exists, use it
    if (expense.baseAmount !== undefined && expense.vatAmount !== undefined) {
      return {
        baseAmount: expense.baseAmount || 0,
        vatAmount: expense.vatAmount || 0,
        totalAmount: expense.totalAmount || expense.amount || 0
      };
    }

    // Calculate VAT breakdown based on expense data
    const totalAmount = expense.totalAmount || expense.amount || 0;
    
    if (expense.vatIncluded === true) {
      // Amount includes VAT - calculate base and VAT
      const baseAmount = totalAmount / 1.15;
      const vatAmount = totalAmount - baseAmount;
      return {
        baseAmount: Math.round(baseAmount * 100) / 100,
        vatAmount: Math.round(vatAmount * 100) / 100,
        totalAmount: totalAmount
      };
    } else if (expense.vatIncluded === false) {
      // Amount excludes VAT - calculate VAT and total
      const baseAmount = totalAmount;
      const vatAmount = totalAmount * 0.15;
      return {
        baseAmount: baseAmount,
        vatAmount: Math.round(vatAmount * 100) / 100,
        totalAmount: Math.round((baseAmount + vatAmount) * 100) / 100
      };
    } else {
      // VAT inclusion unknown - assume total amount is correct
      return {
        baseAmount: totalAmount,
        vatAmount: 0,
        totalAmount: totalAmount
      };
    }
  },

  getCategoryInfo(categoryId, language = 'en') {
    const categories = {
      office: { name: language === 'ar' ? 'مصاريف المكتب' : 'Office Expenses', icon: '🏢' },
      marketing: { name: language === 'ar' ? 'التسويق' : 'Marketing', icon: '📢' },
      salary: { name: language === 'ar' ? 'الرواتب' : 'Salaries', icon: '💰' },
      utility: { name: language === 'ar' ? 'المرافق' : 'Utilities', icon: '⚡' },
      equipment: { name: language === 'ar' ? 'المعدات' : 'Equipment', icon: '🔧' },
      legal: { name: language === 'ar' ? 'قانوني' : 'Legal', icon: '⚖️' },
      travel: { name: language === 'ar' ? 'السفر' : 'Travel', icon: '✈️' },
      insurance: { name: language === 'ar' ? 'التأمين' : 'Insurance', icon: '🛡️' },
      maintenance: { name: language === 'ar' ? 'الصيانة' : 'Maintenance', icon: '🔨' },
      fuel: { name: language === 'ar' ? 'الوقود' : 'Fuel', icon: '⛽' },
      truck_washing: { name: language === 'ar' ? 'غسيل الشاحنات' : 'Truck Washing', icon: '🚿' },
      oil_change: { name: language === 'ar' ? 'تغيير الزيت' : 'Oil Changes', icon: '🛢️' },
      commission: { name: language === 'ar' ? 'العمولة' : 'Commission', icon: '💼' },
      other: { name: language === 'ar' ? 'أخرى' : 'Other', icon: '📋' }
    };
    
    return categories[categoryId] || categories.other;
  },

  getVATCalculationMethod(expense, language = 'en') {
    if (expense.vatIncluded === true) {
      return language === 'ar' ? 'شامل الضريبة' : 'VAT Inclusive';
    } else if (expense.vatIncluded === false) {
      return language === 'ar' ? 'غير شامل الضريبة' : 'VAT Exclusive';
    } else {
      return language === 'ar' ? 'غير محدد' : 'Not Specified';
    }
  },

  getStatusText(status, language = 'en') {
    const statusMap = {
      pending: language === 'ar' ? 'في الانتظار' : 'Pending',
      approved: language === 'ar' ? 'موافق عليه' : 'Approved',
      rejected: language === 'ar' ? 'مرفوض' : 'Rejected',
      paid: language === 'ar' ? 'مدفوع' : 'Paid'
    };
    
    return statusMap[status] || status;
  },

  generateExpenseId(expense) {
    return `EXP${expense.id?.replace(/[^0-9]/g, '').slice(-6).padStart(6, '0') || expense.docId?.replace(/[^0-9]/g, '').slice(-6).padStart(6, '0') || '000001'}`;
  },

  getShipmentReference(expense) {
    if (expense.relatedShipmentId) {
      return expense.relatedShipmentId;
    }
    
    // Try to extract shipment number from description
    const description = expense.description || '';
    const shipmentMatch = description.match(/shipment\s+([^\s\)]+)/i);
    if (shipmentMatch) {
      return shipmentMatch[1];
    }
    
    return '';
  }
};
